import * as React from 'react';
import { IconChevronDown, IconChevronUp, IconLogoTock } from '@tock/icons';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { Theme, css } from '@emotion/react';
import { Typography } from 'bento';

export type SidebarNavItemProps<C extends React.ElementType> = {
  title: string;
  internal?: boolean;
  icon?: React.ReactElement<SvgIconProps>;
  header?: boolean;
  active?: boolean;
  /**
   * Optional component, React.ElementType this specifies the component to be rendered as the wrapper for the SidebarNavItem*/
  component?: C;
} & React.ComponentProps<C>;

const sidebarNavItem = (
  { spacing, borderRadius, colors }: Theme,
  internal: SidebarNavItemProps<'div'>['internal'],
  header: SidebarNavItemProps<'div'>['header']
) => css`
  display: grid;
  grid-template-columns: auto 1fr;
  justify-content: flex-start;
  align-items: center;
  gap: ${spacing(1)}px;
  padding: ${spacing(0.5)}px ${spacing(1)}px;
  border-radius: ${borderRadius[100]};
  min-height: ${spacing(4)}px;
  color: ${internal ? colors.bgInternalBase : 'inherit'};

  & p {
    color: ${internal ? colors.bgInternalBase : 'inherit'};
  }

  &:hover {
    background: ${internal ? colors.bgDefault : colors.bgLight};
  }

  [aria-current='page'] &,
  &[aria-current='page'] {
    color: ${colors.fgContrast};
    background: ${internal ? colors.bgInternalBase : colors.bgPrimaryBase};
  }

  [aria-current='page'] & p {
    color: ${colors.fgContrast};
  }

  ${header &&
  `
  grid-template-columns: auto 1fr auto;
  `}
`;

const sidebarNavItemText = css`
  justify-self: flex-start;
`;

/**
 * @deprecated Deprecated — no pantry replacement available yet.
 * Migration: No direct pantry mapping — please reach out to the Design Systems team on Slack (e.g., #pantry-design-system) for assistance with a pantry replacement.
 */
export const SidebarNavItem = <C extends React.ElementType>({
  title,
  icon,
  internal = false,
  header,
  active,
  component,
  ...rest
}: SidebarNavItemProps<C>) => {
  const Component = component || 'div';
  return (
    <Component css={(theme: Theme) => sidebarNavItem(theme, internal, header)} {...rest}>
      {internal ? <IconLogoTock fontSize="small" /> : icon}
      <Typography variant="bodyStrong" css={sidebarNavItemText}>
        {title}
      </Typography>
      {header && !active && <IconChevronDown fontSize="small" />}
      {header && active && <IconChevronUp fontSize="small" />}
    </Component>
  );
};
